/** @jsx jsx */
import { useContext, useState } from "react"
import Image from "gatsby-image"
import { useTransition, animated as a } from "react-spring"
import { Button, Box, Flex, Text, jsx } from "theme-ui"
import BlockContent from "@sanity/block-content-to-react"

import { Context } from "../../context/MainContext"
import { Eye, InfoNext, InfoPrev } from "../svg"

const styles = {
  container: {
    position: "fixed",
    top: "0px",
    left: "0px",
    height: "100%",
    minHeight: "100vh",
    minHeight: "-webkit-fill-available",
    width: "100vw",
    backgroundColor: "#FFF",
    display: "flex",
    flexDirection: "column",
    margin: "0px",
    padding: "20px 8px",
    zIndex: 99,
    fontSize: "18px",
  },
  content: {
    p: {
      margin: "1em 0",
    },
  },
  controls: {
    p: {
      fontSize: "18px",
    },
    button: {
      backgroundColor: "transparent",
      marginBottom: "1em",
      padding: "0.25em 0.50em",
    },
  },
}

const BlockRenderer = props => {
  if (props.node.style === "normal") {
    return (
      <Text variant="secondary" as="p" color="#333">
        {props.children}
      </Text>
    )
  }
  return BlockContent.defaultSerializers.types.block(props)
}

const serializers = {
  types: { block: BlockRenderer, horizontalRule: () => <hr /> },
}

function FlavorNotesModal({ flavor }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const {
    store: { isFlavorNotesOpen },
    toggleFlavorNotesOpen,
  } = useContext(Context)

  const transitions = useTransition(isFlavorNotesOpen, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    duration: 1000,
  })

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <a.div key={key} style={props} tabIndex="0" sx={styles.container}>
          {/* HEADER */}
          <Box sx={{ width: "100%" }}>
            <Button
              onClick={() => toggleFlavorNotesOpen()}
              sx={{
                height: "55px",
                backgroundColor: "transparent",
                padding: "0px",
              }}
            >
              <Eye
                primary={flavor.secondaryColor}
                secondary={flavor.primaryColor}
              />
              <Text
                as="span"
                variant="secondary"
                sx={{
                  color: flavor.primaryColor,
                  fontSize: "14px",
                  marginLeft: "10px",
                }}
              >
                CLOSE
              </Text>
            </Button>
          </Box>

          {/* INNNER */}
          <Box
            sx={{
              width: "100%",
              flexGrow: 1,
              overflowY: "scroll",
              ...styles.content,
            }}
          >
            <Text
              variant="secondary"
              as="p"
              sx={{ color: flavor.primaryColor }}
            >
              {slideIndex === 0 && flavor.title.toUpperCase()}
              {slideIndex === 1 && "INGREDIENTS"}
              {slideIndex === 2 && "NUTRITION FACTS"}
            </Text>
            {slideIndex === 0 && (
              <BlockContent
                blocks={flavor.description}
                serializers={serializers}
              />
            )}
            {slideIndex === 1 && (
              <Text variant="secondary" as="p" color="#333">
                {flavor.ingredients?.toUpperCase() || ""}
              </Text>
            )}
            {slideIndex === 2 && (
              <Image
                fluid={
                  flavor.nutritionInfoImage.localFile.childImageSharp.fluid
                }
                style={{ width: "100%" }}
                alt="Nutrition Facts"
              />
            )}
          </Box>

          {/* CONTROLS */}
          <Flex
            sx={{
              flexDirection: "column",
              alignItems: "center",
              ...styles.controls,
            }}
          >
            <Box>
              <Button
                onClick={() =>
                  setSlideIndex(slideIndex - 1 < 0 ? 0 : slideIndex - 1)
                }
              >
                <InfoPrev color={flavor.primaryColor} />
              </Button>
              <Button
                onClick={() =>
                  setSlideIndex(slideIndex + 1 > 2 ? 2 : slideIndex + 1)
                }
              >
                <InfoNext color={flavor.primaryColor} />
              </Button>
            </Box>
            <Text
              as="p"
              sx={{ color: flavor.primaryColor }}
              variant="secondary"
            >{`[ ${slideIndex + 1} / 3 ]`}</Text>
          </Flex>
        </a.div>
      )
  )
}

export default FlavorNotesModal
